// src/hooks/useNotifications.js
// Notifiche popup sul dispositivo (Firebase Cloud Messaging) — ottobre 2026.
// Ogni dispositivo su cui la persona dà il permesso viene registrato in userTokens/{uid}
// (campo "dispositivi", uno per dispositivo: prima ne restava solo l'ultimo). Il server
// (functions: pushDaNotifica) manda la notifica per ogni nuovo avviso della campanella a
// tutti i dispositivi registrati, se la persona non le ha spente in Impostazioni → Profilo.
// Su iPhone/iPad funzionano solo con Nexiora installata sulla schermata Home (iOS 16.4+).
import { useEffect, useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, messaging, getToken, onMessage } from "../firebase";
import { VAPID_KEY } from "../utils/configNotifiche";

export const notificheSupportate = () => typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;
export const notificheConfigurate = () => !!VAPID_KEY;

// Chiave del dispositivo nel documento: ricavata dal token (solo lettere e numeri)
const chiaveDispositivo = t => String(t).replace(/[^A-Za-z0-9]/g, "").slice(-40);

/** Registra questo dispositivo per le notifiche della persona (token o null). */
async function registraDispositivo(uid) {
    try {
      if (!messaging || !VAPID_KEY || !uid) return null;
      const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (!currentToken) return null;
      await setDoc(doc(db, "userTokens", uid), {
        uid,
        dispositivi: { [chiaveDispositivo(currentToken)]: { token: currentToken, userAgent: navigator.userAgent, updatedAt: serverTimestamp() } },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      return currentToken;
    } catch (err) {
      console.warn("Notifiche push non disponibili:", err?.message || err);
      return null;
    }
}

export default function useNotifications(currentUser) {
  const [permesso, setPermesso] = useState(null); // "granted"|"denied"|"default"|null
  const [token, setToken] = useState(null);
  const uid = currentUser?.uid;

  useEffect(() => {
    if (!uid || !notificheSupportate()) return;
    const stato = Notification.permission || "default";
    setPermesso(stato);
    if (stato === "granted") registraDispositivo(uid).then(t => { if (t) setToken(t); });
  }, [uid]);

  // Nexiora aperta: il server manda solo dati, la notifica la mostriamo noi
  useEffect(() => {
    if (!messaging) return;
    try {
      return onMessage(messaging, payload => {
        const d = payload.data || payload.notification || {};
        if (d.title && notificheSupportate() && Notification.permission === "granted") {
          const n = new Notification(d.title, { body: d.body || "", icon: "/icon-192.png", tag: d.tag || undefined });
          n.onclick = () => { window.focus(); if (d.url) window.dispatchEvent(new CustomEvent("nexiora:apri-link", { detail: d.url })); n.close(); };
        }
      });
    } catch (e) { return undefined; }
  }, []);

  const richiediPermesso = async () => {
    if (!notificheSupportate()) return "not-supported";
    try {
      const risultato = await Notification.requestPermission();
      setPermesso(risultato);
      if (risultato === "granted" && uid) { const t = await registraDispositivo(uid); if (t) setToken(t); }
      return risultato;
    } catch (e) {
      return "error";
    }
  };

  return { permesso, token, richiediPermesso };
}
